import * as SecureStore from 'expo-secure-store';
import { Platform } from 'react-native';

import { REFRESH_TOKEN_KEY, TOKEN_KEY } from '@/adapters/auth/storageKeys';
import { sentryLogger } from '@/adapters/logger/sentryLogger';

const MIGRATION_FLAG_KEY = 'keychainAccessibilityMigrated';
const MIGRATED_KEYS = [TOKEN_KEY, REFRESH_TOKEN_KEY];

// SecItemUpdate only writes the value, never kSecAttrAccessible, so entries
// created before AFTER_FIRST_UNLOCK was adopted keep the old WHEN_UNLOCKED
// level for as long as they are merely overwritten. Only a delete followed by
// a fresh add applies the new accessibility level.
const secureStoreOptions: SecureStore.SecureStoreOptions = {
  keychainAccessible: SecureStore.AFTER_FIRST_UNLOCK,
};

const migrateKey = async (key: string): Promise<void> => {
  const value = await SecureStore.getItemAsync(key);

  if (!value) {
    return;
  }

  await SecureStore.deleteItemAsync(key);

  try {
    await SecureStore.setItemAsync(key, value, secureStoreOptions);
  } catch (error) {
    // The entry has already been deleted, so losing this write would sign the
    // user out. Put the value back at whatever level the keychain accepts.
    sentryLogger.captureException(error, { context: 'keychainAccessibility.migrate' });
    await SecureStore.setItemAsync(key, value);

    throw error;
  }
};

const migrateKeychainAccessibility = async (): Promise<void> => {
  if (Platform.OS !== 'ios') {
    return;
  }

  const isMigrated = await SecureStore.getItemAsync(MIGRATION_FLAG_KEY);

  if (isMigrated) {
    return;
  }

  try {
    for (const key of MIGRATED_KEYS) {
      await migrateKey(key);
    }

    await SecureStore.setItemAsync(MIGRATION_FLAG_KEY, 'true', secureStoreOptions);
    sentryLogger.addBreadcrumb('Keychain accessibility migrated');
  } catch {
    // Already reported in migrateKey. The flag stays unset so the next launch retries.
  }
};

export { migrateKeychainAccessibility, MIGRATION_FLAG_KEY };
